// Hue / Saturation — Photoshop-style adjustment.
//
// Operates in HSL color space. The algorithm:
//
//   Hue shift:        add shift_degrees (wrapping 0..360)
//   Saturation:       multiply by (1 + satDelta/100)  (clamp 0..1)
//   Lightness:        offset by ligDelta, scaled so endpoints are preserved
//                     (see `adjustLightness` below)
//   Colorize mode:    replace H and S entirely with target values; preserve L
//
// The HSL ↔ RGB conversion uses the standard cylindrical model (H in 0..360,
// S and L in 0..1). Alpha is never touched. Selection masking is delegated
// to `applyFilterInPlace`.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface HueSaturationParams {
  /** Master hue shift in degrees, -180..+180. 0 = no change. */
  hue: number;
  /** Master saturation delta in percent, -100..+100. 0 = no change. */
  saturation: number;
  /** Master lightness delta in percent, -100..+100. 0 = no change. */
  lightness: number;
  /** When true, the image is colorized with the given hue/saturation values. */
  colorize: boolean;
  /** Target hue for colorize mode (0..360). Ignored when colorize is false. */
  colorizeHue: number;
  /** Target saturation for colorize mode (0..100). Ignored when colorize is false. */
  colorizeSat: number;
  /** Per-band adjustments. Each band has its own hue/saturation/lightness. */
  bands?: {
    reds: { hue: number; saturation: number; lightness: number };
    yellows: { hue: number; saturation: number; lightness: number };
    greens: { hue: number; saturation: number; lightness: number };
    cyans: { hue: number; saturation: number; lightness: number };
    blues: { hue: number; saturation: number; lightness: number };
    magentas: { hue: number; saturation: number; lightness: number };
  };
}

const DEFAULT_PARAMS: HueSaturationParams = {
  hue: 0,
  saturation: 0,
  lightness: 0,
  colorize: false,
  colorizeHue: 0,
  colorizeSat: 25,
  bands: {
    reds: { hue: 0, saturation: 0, lightness: 0 },
    yellows: { hue: 0, saturation: 0, lightness: 0 },
    greens: { hue: 0, saturation: 0, lightness: 0 },
    cyans: { hue: 0, saturation: 0, lightness: 0 },
    blues: { hue: 0, saturation: 0, lightness: 0 },
    magentas: { hue: 0, saturation: 0, lightness: 0 },
  },
};

// ---------------------------------------------------------------------------
// HSL ↔ RGB conversion (standard cylindrical model)
// ---------------------------------------------------------------------------

interface HSL {
  h: number; // 0..360
  s: number; // 0..1
  l: number; // 0..1
}

function rgbToHsl(r: number, g: number, b: number): HSL {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === rn) {
    h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  } else if (max === gn) {
    h = ((bn - rn) / d + 2) * 60;
  } else {
    h = ((rn - gn) / d + 4) * 60;
  }

  return { h, s: s, l };
}

function hslToRgb(hsl: HSL): [number, number, number] {
  const { h, s, l } = hsl;

  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }

  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;

  let r1 = 0, g1 = 0, b1 = 0;
  if (hue < 60) { r1 = c; g1 = x; b1 = 0; }
  else if (hue < 120) { r1 = x; g1 = c; b1 = 0; }
  else if (hue < 180) { r1 = 0; g1 = c; b1 = x; }
  else if (hue < 240) { r1 = 0; g1 = x; b1 = c; }
  else if (hue < 300) { r1 = x; g1 = 0; b1 = c; }
  else { r1 = c; g1 = 0; b1 = x; }

  return [
    Math.round((r1 + m) * 255),
    Math.round((g1 + m) * 255),
    Math.round((b1 + m) * 255),
  ];
}

// ---------------------------------------------------------------------------
// Lightness adjustment — endpoint-preserving model similar to Brightness.
//
// For positive lightness (+L%): lift dark pixels proportionally more.
//   L_range: +0..+100 maps to coefficient 0..1
//   v_out = v + L * (1 - v)    (where v is normalized 0..1)
//
// For negative lightness (-L%): crush light pixels proportionally more.
//   v_out = v + L * v           (L is negative, so this reduces v)
// ---------------------------------------------------------------------------

function adjustLightness(l: number, lightPct: number): number {
  if (lightPct === 0) return l;
  if (lightPct > 0) {
    const factor = lightPct / 100;
    return l + factor * (1 - l);
  }
  const factor = -lightPct / 100; // positive 0..1
  return l - factor * l;
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const HueSaturation: Filter<HueSaturationParams> = {
  id: "hue-saturation",
  displayName: "Hue/Saturation",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: HueSaturationParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const hueShift = clampRange(params.hue, -180, 180);
    const satDelta = clampRange(params.saturation, -100, 100);
    const ligDelta = clampRange(params.lightness, -100, 100);
    const colorize = params.colorize;
    const colHue = ((params.colorizeHue % 360) + 360) % 360;
    const colSat = clampRange(params.colorizeSat, 0, 100) / 100;

    // Per-band adjustments
    const bands = params.bands;
    const bandRanges: Array<{ name: string; hueCenter: number; hueWidth: number; falloff: number }> = [
      { name: "reds", hueCenter: 0, hueWidth: 30, falloff: 15 },
      { name: "yellows", hueCenter: 60, hueWidth: 30, falloff: 15 },
      { name: "greens", hueCenter: 120, hueWidth: 30, falloff: 15 },
      { name: "cyans", hueCenter: 180, hueWidth: 30, falloff: 15 },
      { name: "blues", hueCenter: 240, hueWidth: 30, falloff: 15 },
      { name: "magentas", hueCenter: 300, hueWidth: 30, falloff: 15 },
    ];

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      let hsl = rgbToHsl(r, g, b);
      const origH = hsl.h;

      if (colorize) {
        hsl.h = colHue;
        hsl.s = colSat;
      } else {
        // Apply per-band adjustments first
        if (bands) {
          let bandHue = 0, bandSat = 0, bandLig = 0;
          for (const br of bandRanges) {
            const band = bands[br.name as keyof typeof bands];
            if (!band || (band.hue === 0 && band.saturation === 0 && band.lightness === 0)) continue;
            const weight = hueBandWeight(origH, br.hueCenter, br.hueWidth, br.falloff);
            if (weight > 0) {
              bandHue += band.hue * weight;
              bandSat += band.saturation * weight;
              bandLig += band.lightness * weight;
            }
          }
          if (bandHue !== 0) hsl.h = ((hsl.h + bandHue) % 360 + 360) % 360;
          if (bandSat !== 0) hsl.s = clamp01(hsl.s * (1 + bandSat / 100));
          if (bandLig !== 0) hsl.l = clamp01(adjustLightness(hsl.l, bandLig));
        }

        // Then apply master adjustments
        if (hueShift !== 0) {
          hsl.h = ((hsl.h + hueShift) % 360 + 360) % 360;
        }
        if (satDelta !== 0) {
          hsl.s = clamp01(hsl.s * (1 + satDelta / 100));
        }
      }

      if (ligDelta !== 0) {
        hsl.l = clamp01(adjustLightness(hsl.l, ligDelta));
      }

      const [ro, go, bo] = hslToRgb(hsl);
      dst[o] = ro;
      dst[o + 1] = go;
      dst[o + 2] = bo;
      dst[o + 3] = a;
    }

    return out;
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Compute the weight (0..1) of a pixel's hue within a color band.
 * Uses a smooth falloff at the band edges for natural transitions.
 */
function hueBandWeight(hue: number, center: number, width: number, falloff: number): number {
  let diff = Math.abs(hue - center);
  if (diff > 180) diff = 360 - diff; // wrap around
  const halfWidth = width + falloff;
  if (diff > halfWidth) return 0;
  if (diff <= width) return 1;
  // Smooth falloff
  return 1 - (diff - width) / falloff;
}
